import { Alert, Card, Checkbox, Col, Form, Input, Row, Select, Switch } from 'antd'
import React, { useState, useEffect, useCallback } from 'react'
import { Breadcrumb, Button, Container } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { useForm } from 'antd/es/form/Form';
import { useToasts } from '../../../contexts/toasts';
import { TailSpin } from 'react-loader-spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { useMemo } from 'react';
import { Organization, Permission } from '../../../models/ControlPanel';
import { CustomBootstrapLinkType } from '../../../utils';
import { Helmet } from 'react-helmet';

const formLayout = {
  wrapperCol: {
    xs: 24,
    sm: 15,
  },
  labelCol: {
    xs: 24,
    sm: 5,
  },
};

export default function UserEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [userOrganizations, setUserOrganizations] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>();
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>();
  const [error, setError] = useState(false);
  const [form] = useForm();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const organizationsOptions = useMemo(() => organizations?.map(group => ({ label: group.name, value: group.id })), [organizations]);
  const permissionsOptions = useMemo(() => permissions?.map(group => ({ label: group.name, value: group.id })), [permissions]);

  useEffect(() => {
    if (id) initUser();

    initOrganizations();
    initPermissions();
    // eslint-disable-next-line
  }, [id]);

  const initUserGroups = useCallback(() => {
    ControlPanelManager.getUserGroups(id!)
      .then(
        ({ data }) => {
          setLoading(false);
          const pp = data.filter(group => group.path.startsWith('/Permissions')).map(group => group.id);
          setUserPermissions(pp);
          const orgs = data.filter(group => group.path.startsWith('/Organizations')).map(group => group.id);
          setUserOrganizations(orgs);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-groups-error', message, `User Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast]);

  useEffect(() => {
    if (organizations && id) initUserGroups();
  }, [organizations, id, initUserGroups]);

  const initUser = () => {
    ControlPanelManager.getUser(id!)
      .then(
        ({ data }) => form.setFieldsValue(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initPermissions = () => {
    ControlPanelManager.getPermissions()
      .then(
        ({ data }) => setPermissions(data),
        (err) => {
          setPermissions([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('permissions-error', message, `Permissions Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initOrganizations = () => {
    ControlPanelManager.getOrganizations()
      .then(
        ({ data }) => setOrganizations(data),
        (err) => {
          setOrganizations([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const addUserToGroup = (permissionsMode: boolean) => (newGroup: string) => {
    showLoader();

    ControlPanelManager.addUserToGroup(id!, newGroup)
      .then(
        () => {
          hideLoader();
          permissionsMode ? setUserPermissions(pp => pp.concat(newGroup)) : setUserOrganizations(orgs => orgs.concat(newGroup));
          showToast(
            'user-joined-group',
            permissionsMode ? 'Role successfully added to user' : 'User successfully joined organization',
            `${permissionsMode ? 'Roles' : 'Organizations'} Success`,
            'success'
          );
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const removeUserFromGroup = (permissionsMode: boolean) => (oldGroup: string) => {
    showLoader();

    ControlPanelManager.removeUserFromGroup(id!, oldGroup)
      .then(
        () => {
          hideLoader();
          permissionsMode
            ? setUserPermissions(pp => pp.filter(org => org !== oldGroup))
            : setUserOrganizations(orgs => orgs.filter(org => org !== oldGroup));
          showToast(
            'user-left-group',
            permissionsMode ? 'Role successfully removed from user' : 'User successfully left organization',
            `${permissionsMode ? 'Roles' : 'Organizations'} Success`,
            'success'
          );
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }


  const handleSubmit = (values: any) => {
    showLoader();

    const body = { ...values };
    if (!id) {
      // setting credentials object based on password (user registration)
      body.credentials = [{ type: 'password', value: values.password, temporary: false }];
      delete body.password;
      // setting organizations and permissions
      if (userPermissions.length) body.permissions = permissions!.filter(p => userPermissions.includes(p.id!)).map(p => p.name);
      if (userOrganizations.length) {
        body.organizations = organizations!
          .filter(o => userOrganizations.includes(o.id!))
          .map((org, index) => index === 0 ? ({ name: org.name, default: true }) : org.name);
      }
    }

    const promise = id
      ? ControlPanelManager.updateUser({ ...body, id })
      : ControlPanelManager.createUser(body);

    promise.then(
      ({ data }) => {
        showToast('user-success', `User ${id ? 'updated' : 'created'} successfully`, 'User Success', 'success');
        hideLoader();
        navigate(`/control-panel/users-management/${data.id}`);
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('user-submit-error', message, `User ${id ? 'Update' : 'Creation'} Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }

  const renderUser = () => {
    return (
      <Form {...formLayout} form={form} onFinish={handleSubmit}>
        <Form.Item
          label="Username"
          name="username"
          rules={[{ required: true, message: "Username is required" }]}
        >
          <Input placeholder="Username" disabled={id !== undefined} />
        </Form.Item>

        {!id && (
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input placeholder="Password" />
          </Form.Item>
        )}

        <Form.Item label="Email">
          <Row align="middle">
            <Col xs={24} md={12} className="pe-md-3 mb-2 mb-md-0">
              <Form.Item noStyle name="email">
                <Input placeholder="Email" />
              </Form.Item>
            </Col>

            <Form.Item noStyle name="emailVerified" valuePropName="checked">
              <Checkbox className="ms-2">Email verified</Checkbox>
            </Form.Item>

            {!id && (
              <Form.Item noStyle name="sendEmail" valuePropName="checked">
                <Checkbox className="ms-2">Send Email</Checkbox>
              </Form.Item>
            )}

            <Form.Item noStyle name="enabled" valuePropName="checked">
              <Switch className="ms-2" />
            </Form.Item>
            <span className="ms-2">Enable User</span>
          </Row>
        </Form.Item>


        <Form.Item label="First Name" name="firstName">
          <Input placeholder="User First Name" />
        </Form.Item>

        <Form.Item label="Last Name" name="lastName">
          <Input placeholder="User Last Name" />
        </Form.Item>

        <Form.Item label="Roles">
          <Select
            placeholder="Select User Roles"
            value={userPermissions}
            allowClear
            mode="multiple"
            options={permissionsOptions}
            onChange={id ? undefined : setUserPermissions}
            onSelect={id ? addUserToGroup(true) : undefined}
            onDeselect={id ? removeUserFromGroup(true) : undefined}
            loading={!permissionsOptions}
          />
        </Form.Item>

        <Form.Item label="Organizations" help="The first organization will be trated as the default one.">
          <Select
            placeholder="Select User Organizations"
            value={userOrganizations}
            allowClear
            showSearch
            mode="multiple"
            options={organizationsOptions}
            onChange={id ? undefined : setUserOrganizations}
            onSelect={id ? addUserToGroup(false) : undefined}
            onDeselect={id ? removeUserFromGroup(false) : undefined}
            loading={!organizationsOptions}
          />
        </Form.Item>

        <br />

        <div className="d-flex justify-content-between">
          <Button as={Link as unknown as CustomBootstrapLinkType} to="/control-panel/users-management" variant="light" className="border" type="button" size="sm">
            <FontAwesomeIcon icon={solid('chevron-left')} />
            <span className="ms-2">Cancel</span>
          </Button>
          <Button variant="success" size="sm" type="submit">
            <FontAwesomeIcon icon={solid('check')} />
            <span className="ms-2">Confirm</span>
          </Button>
        </div>
      </Form>
    )
  }

  const renderAsyncContent = () => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occured while retrieving user. Try again later."
      />
    )

    return loading
      ? (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      )
      : renderUser()
  }

  return (
    <Container className="py-3">
      <Helmet>
        <title>Control Panel - User {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel' }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel/users-management' }}>User Management</Breadcrumb.Item>
        {id && <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/control-panel/users-management/${id}` }}>{id}</Breadcrumb.Item>}
        <Breadcrumb.Item active>{id ? 'Edit' : 'Create'} User</Breadcrumb.Item>
      </Breadcrumb>

      <Card title={id ? 'Edit User' : 'New User'}>{renderAsyncContent()}</Card>
    </Container>
  )
}